import { useEffect } from 'react'

/**
 * Custom hook to lock/unlock body scroll when a drawer or modal is open
 * This prevents the background page from scrolling when a drawer is open
 * Uses overflow:hidden approach instead of position:fixed to avoid breaking portals
 * 
 * @param {boolean} isOpen - Whether the drawer/modal is open
 * @param {number} zIndex - Optional z-index for multiple overlays (higher = more priority)
 */
export const useBodyScrollLock = (isOpen, zIndex = 1) => {
  useEffect(() => {
    if (!isOpen) return

    // Store original styles for both body and html
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalPaddingRight = document.body.style.paddingRight

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    // Lock scroll on BOTH body and html elements
    // This ensures the page scroll is completely disabled
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    
    // Add padding to compensate for scrollbar disappearing
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    // Add a data attribute to track active locks (for nested drawers)
    const lockCount = parseInt(document.body.dataset.scrollLockCount || '0') + 1
    document.body.dataset.scrollLockCount = lockCount.toString()
    document.body.dataset.scrollLockZIndex = Math.max(
      parseInt(document.body.dataset.scrollLockZIndex || '0'),
      zIndex
    ).toString()

    // Cleanup function to restore scroll
    return () => {
      const currentLockCount = parseInt(document.body.dataset.scrollLockCount || '0')
      const newLockCount = Math.max(0, currentLockCount - 1)
      document.body.dataset.scrollLockCount = newLockCount.toString()

      // Only restore scroll if no other locks are active
      if (newLockCount === 0) {
        // Restore original styles for both body and html
        document.body.style.overflow = originalBodyOverflow || ''
        document.documentElement.style.overflow = originalHtmlOverflow || ''
        document.body.style.paddingRight = originalPaddingRight || ''

        // Clean up data attributes
        delete document.body.dataset.scrollLockCount
        delete document.body.dataset.scrollLockZIndex
      }
    }
  }, [isOpen, zIndex])
}

export default useBodyScrollLock

